import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import BaseModal, { modalStyles, HoverButton } from './BaseModal';
import ModalStickyFooter from '../ui/ModalStickyFooter';
import { FontAwesome6 } from '@expo/vector-icons';
import { colors, typography } from '../../styles/theme';

interface PrivacyPolicyProps {
  visible: boolean;
  onClose: () => void;
  onBack: () => void;
}

export default function PrivacyPolicyModal({ visible, onClose, onBack }: PrivacyPolicyProps) {
  return (
    <BaseModal
      visible={visible}
      onClose={onClose}
      header={(
        <View style={styles.headerRow}>
          <TouchableOpacity onPress={onBack} style={styles.backButton}>
            <FontAwesome6 name="arrow-left" size={20} color={colors.text} solid />
          </TouchableOpacity>
          <Text style={[modalStyles.headerText, { flex: 1, marginBottom: 0 }]}>Privacy Policy</Text>
          <View style={{ width: 44 }} />
        </View>
      )}
      footer={(
        <ModalStickyFooter>
          <HoverButton
            style={modalStyles.secondaryButton}
            textStyle={modalStyles.secondaryButtonText}
            onPress={onClose}
          >
            Close
          </HoverButton>
        </ModalStickyFooter>
      )}
    >
      <View style={styles.container}>
        <Text style={styles.lastUpdated}>Last Updated: May 2026</Text>

        <Text style={styles.introParagraph}>
          Welcome to Piclo ("we," "our," or "us"). We are committed to protecting your privacy. This policy explains how we handle your data, particularly regarding device hardware and personal information.
        </Text>

        <Text style={styles.sectionHeader}>1. Camera and Image Processing (Edge Computing)</Text>
        <Text style={styles.paragraph}>
          Piclo is a camera-based web application. It requires access to your device's camera to function.
        </Text>
        <View style={styles.bulletContainer}>
          <Text style={styles.bulletItem}>
            • <Text style={styles.boldText}>Zero Cloud Storage:</Text> We do not collect, transmit, or store your camera feed, video stream, or captured photos on our servers.
          </Text>
          <Text style={styles.bulletItem}>
            • <Text style={styles.boldText}>Local Processing:</Text> All image capture, color evaluation, masking, and visual processing occurs entirely locally on your device (edge computing).
          </Text>
          <Text style={styles.bulletItem}>
            • <Text style={styles.boldText}>Local Save Data:</Text> The final composite artwork and your gameplay progression are stored directly in your browser's local storage. If you clear your browser cache, your unsaved progress will be lost.
          </Text>
        </View>

        <Text style={styles.sectionHeader}>2. Personal Information and Payments</Text>
        <Text style={styles.paragraph}>
          You do not need to create an account to play the free daily puzzles.
        </Text>
        <Text style={styles.paragraph}>
          If you choose to purchase premium puzzle packs or Piclo Play, all payments are processed securely by our Merchant of Record, Paddle.com. We do not collect, view, or store your credit card or financial information.
        </Text>
        <Text style={styles.paragraph}>
          We collect your email address at checkout solely for the purpose of generating a secure "magic link." This link allows you to synchronize your unlocked purchases across multiple devices.
        </Text>

        <Text style={styles.sectionHeader}>3. Analytics and Tracking</Text>
        <Text style={styles.paragraph}>
          We use lightweight, privacy-focused analytics (such as GoatCounter) to track aggregate, anonymized page views and interactions. We do not use tracking cookies for targeted advertising, and we will never sell your personal data to third parties.
        </Text>

        <Text style={styles.sectionHeader}>4. Contact Us</Text>
        <Text style={styles.paragraph}>
          If you have questions about this privacy policy or wish to request the deletion of your email address from our entitlement database, please contact us at support@piclo.art.
        </Text>
      </View>
    </BaseModal>
  );
}

const styles = StyleSheet.create({
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    width: '100%',
  },
  backButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    marginBottom: 24,
  },
  lastUpdated: {
    ...typography.body,
    fontSize: 14,
    color: colors.textMuted,
    fontStyle: 'italic',
    marginBottom: 16,
  },
  introParagraph: {
    ...typography.bodyBold,
    color: colors.text,
    fontSize: 16,
    lineHeight: 22,
    marginBottom: 20,
  },
  sectionHeader: {
    ...typography.bodyBold,
    fontSize: 18,
    color: colors.text,
    marginTop: 16,
    marginBottom: 8,
  },
  paragraph: {
    ...typography.body,
    color: colors.textMuted,
    fontSize: 15,
    lineHeight: 22,
    marginBottom: 12,
    textAlign: 'left',
  },
  bulletContainer: {
    marginLeft: 8,
    marginBottom: 16,
  },
  bulletItem: {
    ...typography.body,
    color: colors.textMuted,
    fontSize: 15,
    lineHeight: 22,
    marginBottom: 8,
  },
  boldText: {
    ...typography.bodyBold,
    color: colors.text,
  },
});
